import type {
  AttendeeConferenceReview,
  AttendeeConferenceReviewDraftAssistance,
  AttendeeConferenceReviewEntry,
  AttendeeConferenceReviewEvidence,
  AttendeeConferenceReviewResponse,
  AttendeeConferenceReviewValues,
  PublicConventionGuide,
} from "@verity-connect/contracts";
import { useEffect, useState, type KeyboardEvent, type ReactNode } from "react";
import { conferenceReviewPlainText, LearningDocumentActions } from "./LearningDocumentActions";
import { Link } from "./router";
import { SchoolLearningDisclosureNotice } from "./SchoolLearningDisclosureNotice";

export type ReviewSection = "direction" | "connections" | "questions" | "action";
const REVIEW_TABS: readonly [ReviewSection, string][] = [["direction", "Direction"], ["connections", "Connections"], ["questions", "Questions"], ["action", "Action"]];

export function ConferenceReview({ guide, signedIn }: { guide: PublicConventionGuide; signedIn: boolean }) {
  const reviewPath = `/api/attendee/events/${encodeURIComponent(guide.event.slug)}/conference-review`;
  const [review, setReview] = useState<AttendeeConferenceReview | null>(null);
  const [evidence, setEvidence] = useState<AttendeeConferenceReviewEvidence | null>(null);
  const [assistance, setAssistance] = useState<AttendeeConferenceReviewDraftAssistance | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "unavailable">("loading");
  const [section, setSection] = useState<ReviewSection>("direction");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AttendeeConferenceReviewValues | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => { document.title = `Convention Wrap-Up · ${guide.event.name}`; }, [guide.event.name]);

  useEffect(() => {
    if (!signedIn) { setLoadState("ready"); return; }
    const controller = new AbortController();
    setLoadState("loading");
    void loadReview(reviewPath, controller.signal).then((result) => {
      setReview(result.review);
      setEvidence(result.evidence);
      setAssistance(result.draftAssistance);
      setLoadState("ready");
    }).catch(() => { if (!controller.signal.aborted) setLoadState("unavailable"); });
    return () => controller.abort();
  }, [reviewPath, signedIn]);

  useEffect(() => {
    const state = assistance?.request?.state;
    if (state !== "queued" && state !== "processing") return;
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      void loadReview(reviewPath, controller.signal).then((result) => {
        setReview(result.review);
        setEvidence(result.evidence);
        setAssistance(result.draftAssistance);
      }).catch(() => { if (!controller.signal.aborted) setMessage("Your work is safe. This page will update when your connection returns."); });
    }, 1_500);
    return () => { window.clearInterval(timer); controller.abort(); };
  }, [assistance?.request?.state, assistance?.request?.updatedAt, reviewPath]);

  const requestDraft = async () => {
    if (requesting) return;
    setRequesting(true);
    setMessage(null);
    try {
      const response = await fetch(`${reviewPath}/draft`, { method: "POST", credentials: "same-origin" });
      if (!response.ok) throw new Error(await responseMessage(response, "The app cannot create your Convention Wrap-Up right now."));
      setAssistance(await response.json() as AttendeeConferenceReviewDraftAssistance);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The app cannot create your Convention Wrap-Up right now.");
    } finally { setRequesting(false); }
  };

  const beginReview = (values: AttendeeConferenceReviewValues) => {
    setDraft(copyValues(values));
    setEditing(true);
    setMessage("Read each section and change anything you want. Nothing changes until you save.");
  };

  const save = async () => {
    if (!draft || saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(reviewPath, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id: crypto.randomUUID(), baseRevision: review?.revision ?? 0, values: cleanValues(draft) }),
      });
      if (!response.ok) throw new Error(await responseMessage(response, "Your Convention Wrap-Up could not be saved."));
      setReview(await response.json() as AttendeeConferenceReview);
      setEditing(false);
      setMessage("Your Convention Wrap-Up is saved.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Your Convention Wrap-Up could not be saved.");
    } finally { setSaving(false); }
  };

  if (!signedIn) return <ConferenceReviewGate title="Sign in to open your Wrap-Up" body="It is built from the private session records saved to your account, so it has nothing to read until you are signed in." action={<Link className="primary-action" href="/account">Sign in</Link>} />;
  if (loadState === "loading") return <ConferenceReviewGate title="Opening your Convention Wrap-Up…" body="Loading your saved session records." />;
  if (loadState === "unavailable" || !evidence) return <ConferenceReviewGate title="Your Convention Wrap-Up is unavailable." body="Your session records remain safe. Try again when your connection returns." action={<button className="primary-action" type="button" onClick={() => window.location.reload()}>Try again</button>} />;

  const readyDraft = assistance?.request?.state === "ready" ? assistance.request.values : null;
  const working = assistance?.request?.state === "queued" || assistance?.request?.state === "processing";
  const failed = assistance?.request?.state === "failed";
  const values = editing && draft ? draft : review?.values ?? readyDraft;

  return (
    <article className="page conference-review-page">
      {/* Every other page under My WVCEA carries this and this one did not, which in the
          installed application is a trap rather than an inconvenience: each tab is its own
          web view, so a tab left on this page stays on it — through switching tabs, through
          signing out, through relaunching. There was no way back to My WVCEA at all. */}
      <Link className="back-link subpage-back" href="/my-wvcea">← My WVCEA</Link>
      <header className="conference-review-hero">
        <p>Learning Companion</p>
        <h1 data-page-heading tabIndex={-1}>My Convention Wrap-Up</h1>
        <span>The ideas, questions, and next steps you chose to keep.</span>
        <div><strong>{evidence.coverage.sessionCount} finished session {evidence.coverage.sessionCount === 1 ? "record" : "records"}</strong><small>{evidence.coverage.supportedSessionCount} with session materials</small></div>
      </header>
      <SchoolLearningDisclosureNotice eventSlug={guide.event.slug} signedIn={signedIn} />

      {!evidence.coverage.draftAllowed && !review ? (
        <section className="conference-review-progress" aria-labelledby="review-progress-title">
          <p>Almost ready</p>
          <h2 id="review-progress-title">Finish one more session record to create your Convention Wrap-Up.</h2>
          <div className="conference-review-meter" aria-label={`${evidence.coverage.sessionCount} of ${evidence.coverage.requiredSessionCount} required session records`}><span style={{ width: `${Math.min(100, evidence.coverage.sessionCount / evidence.coverage.requiredSessionCount * 100)}%` }} /></div>
          <p>{evidence.coverage.explanation}</p>
          <Link className="primary-action" href="/my-wvcea">Open my session records</Link>
        </section>
      ) : (
        <>
          <section className={`conference-review-status ${readyDraft ? "ready" : working ? "working" : failed ? "failed" : "available"}`} aria-live="polite">
            <div><small>Convention Wrap-Up</small><strong>{review ? "Your wrap-up is saved"
              : readyDraft ? "Your Convention Wrap-Up is ready"
                : working ? "Writing your Convention Wrap-Up"
                  : failed ? "Your Convention Wrap-Up could not be written"
                  : "Bring your session records together"}</strong>
              <p>{review ? `Saved ${formatDate(review.updatedAt)}`
                : readyDraft ? "Read each section and change anything you want before you save."
                  : working ? "You can leave this page. We will keep working and save the result to your account."
                    : failed ? assistance?.request?.detail ?? "The app stopped before it finished. Your session records are still safe."
                      : "The app will find connections across your finished session records. You decide what to keep."}</p></div>
            {!editing && (review
              ? <button type="button" onClick={() => beginReview(review.values)}>Edit wrap-up</button>
              : readyDraft ? <button type="button" onClick={() => beginReview(readyDraft)}>Open wrap-up</button>
                : assistance?.canRequest ? <button type="button" onClick={() => { void requestDraft(); }} disabled={requesting}>{requesting ? "Starting…" : failed ? "Try again" : "Create my wrap-up"}</button>
                  : working ? <span aria-label="Writing your Convention Wrap-Up">•••</span> : null)}
          </section>

          {values && <>
            {!editing && review && <LearningDocumentActions text={conferenceReviewPlainText(guide.event.name, review.values)} />}
            <div className="conference-review-screen-content">
              <ReviewTabs selected={section} onSelect={setSection} />
              <section className="conference-review-workspace" id="conference-review-panel" role="tabpanel" aria-labelledby={`conference-review-tab-${section}`}>
                {editing && draft
                  ? <ConferenceReviewEditor section={section} values={draft} onChange={setDraft} />
                  : <ConferenceReviewSection section={section} values={values} evidence={evidence} />}
              </section>
            </div>
            {!editing && review && <ConferenceReviewPrintDocument eventName={guide.event.name} values={review.values} evidence={evidence} />}
            {editing && <footer className="conference-review-actions"><button type="button" onClick={() => { setEditing(false); setDraft(null); setMessage(null); }} disabled={saving}>Cancel</button><button className="primary-action" type="button" onClick={() => { void save(); }} disabled={saving}>{saving ? "Saving…" : "Save wrap-up"}</button></footer>}
          </>}
        </>
      )}

      <p className="conference-review-message" role="status" aria-live="polite" aria-atomic="true">{message}</p>
      <section className="conference-review-sources" aria-labelledby="conference-sources-title">
        <header><p>Included sessions</p><h2 id="conference-sources-title">Session records in this wrap-up</h2></header>
        {evidence.sources.length === 0 ? <p>No finished session records yet.</p> : <ul>{evidence.sources.map((source) => <li key={source.itemId}><Link href={`/my-wvcea/learning/${encodeURIComponent(source.itemId)}`}>{source.title}</Link><span>Saved record{source.officialDescriptionIncluded ? " · official description" : ""}{source.presenterResourceIds.length ? ` · ${source.presenterResourceIds.length} presenter ${source.presenterResourceIds.length === 1 ? "item" : "items"}` : ""}</span></li>)}</ul>}
      </section>
      <p className="device-note"><span aria-hidden="true">●</span>WVCEA staff cannot read this Wrap-Up through routine Event tools. If your registration has a linked School disclosure, the finalized Wrap-Up becomes part of that School record after the Event.</p>
    </article>
  );
}

export function ReviewTabs({ selected, onSelect }: { selected: ReviewSection; onSelect: (section: ReviewSection) => void }) {
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = REVIEW_TABS.length - 1;
    const next = event.key === "ArrowRight" ? index === last ? 0 : index + 1
      : event.key === "ArrowLeft" ? index === 0 ? last : index - 1
        : event.key === "Home" ? 0 : event.key === "End" ? last : null;
    if (next === null) return;
    event.preventDefault();
    const value = REVIEW_TABS[next]![0];
    onSelect(value);
    document.getElementById(`conference-review-tab-${value}`)?.focus();
  };
  return <div className="conference-review-tabs" role="tablist" aria-label="Convention Wrap-Up sections">{REVIEW_TABS.map(([value, label], index) => <button id={`conference-review-tab-${value}`} key={value} type="button" role="tab" aria-selected={selected === value} aria-controls="conference-review-panel" tabIndex={selected === value ? 0 : -1} onKeyDown={(event) => move(event, index)} onClick={() => onSelect(value)}>{label}</button>)}</div>;
}

function ConferenceReviewPrintDocument({ eventName, values, evidence }: { eventName: string; values: AttendeeConferenceReviewValues; evidence: AttendeeConferenceReviewEvidence }) {
  return <div className="conference-review-print-document">
    <header><p>{eventName}</p><h2>My Convention Wrap-Up</h2></header>
    {REVIEW_TABS.map(([section, label]) => <section key={section}><h3>{label}</h3><ConferenceReviewSection section={section} values={values} evidence={evidence} /></section>)}
  </div>;
}

export function ConferenceReviewSection({ section, values, evidence }: { section: ReviewSection; values: AttendeeConferenceReviewValues; evidence: AttendeeConferenceReviewEvidence }) {
  const sections = sectionFields(section, values);
  return <div className={`conference-review-section companion-review companion-review-${section}`}>{sections.map(({ title, kind, entries, empty }) => <section className={`companion-review-block ${kind}`} key={title}><h2>{title}</h2>{entries.length ? entries.map((entry) => <article key={entry.id}><p>{entry.text}</p><SourceLabels entry={entry} evidence={evidence} /></article>) : <p className="conference-review-empty">{empty}</p>}</section>)}</div>;
}

function ConferenceReviewEditor({ section, values, onChange }: { section: ReviewSection; values: AttendeeConferenceReviewValues; onChange: (values: AttendeeConferenceReviewValues) => void }) {
  const setSingle = (field: "centralDirection" | "actionDirection", text: string) => onChange({ ...values, [field]: editedSingle(values[field], text) });
  const setList = (field: "themes" | "importantIdeas" | "changesInThinking" | "tensions" | "implications", text: string) => onChange({ ...values, [field]: editedList(values[field], text, field) });
  if (section === "direction") return <div className="conference-review-editor"><ReviewField label="Main idea" hint="What ties the convention together for you?" value={values.centralDirection?.text ?? ""} onChange={(text) => setSingle("centralDirection", text)} rows={4} /><ReviewField label="Ideas to keep" hint="One idea per line" value={values.importantIdeas.map((entry) => entry.text).join("\n")} onChange={(text) => setList("importantIdeas", text)} rows={8} /></div>;
  if (section === "connections") return <div className="conference-review-editor"><ReviewField label="Connections across sessions" hint="One connection per line" value={values.themes.map((entry) => entry.text).join("\n")} onChange={(text) => setList("themes", text)} rows={7} /><ReviewField label="Changes in my thinking" hint="Use your own words. Leave this blank if your thinking did not change." value={values.changesInThinking.map((entry) => entry.text).join("\n")} onChange={(text) => setList("changesInThinking", text)} rows={7} /></div>;
  if (section === "questions") return <div className="conference-review-editor"><ReviewField label="Questions I still have" hint="One question per line" value={values.tensions.map((entry) => entry.text).join("\n")} onChange={(text) => setList("tensions", text)} rows={7} /><ReviewField label="What this could mean for my work" hint="How might this affect your classroom, team, or school?" value={values.implications.map((entry) => entry.text).join("\n")} onChange={(text) => setList("implications", text)} rows={7} /></div>;
  return <div className="conference-review-editor"><ReviewField label="What I want to try" hint="" value={values.actionDirection?.text ?? ""} onChange={(text) => setSingle("actionDirection", text)} rows={6} /></div>;
}

function ReviewField({ label, hint, value, rows, onChange }: { label: string; hint: string; value: string; rows: number; onChange: (value: string) => void }) {
  return <label><span>{label}</span>{hint && <small>{hint}</small>}<textarea rows={rows} maxLength={8000} value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function SourceLabels({ entry, evidence }: { entry: AttendeeConferenceReviewEntry; evidence: AttendeeConferenceReviewEvidence }) {
  const labels = entry.sourceItemIds.map((id) => evidence.sources.find((source) => source.itemId === id)?.title).filter((title): title is string => Boolean(title));
  return <small>{labels.length ? labels.join(" · ") : entry.origin === "attendee" ? "Added by you" : "Source unavailable"}</small>;
}

function sectionFields(section: ReviewSection, values: AttendeeConferenceReviewValues) {
  if (section === "direction") return [
    { title: "Main idea", kind: "lead", entries: values.centralDirection ? [values.centralDirection] : [], empty: "No main idea has been added yet." },
    { title: "Ideas to keep", kind: "ideas", entries: values.importantIdeas, empty: "No ideas have been added yet." },
  ];
  if (section === "connections") return [
    { title: "Connections across sessions", kind: "themes", entries: values.themes, empty: "No connections have been added yet." },
    { title: "Changes in my thinking", kind: "changes", entries: values.changesInThinking, empty: "Add this in your own words if your thinking changed." },
  ];
  if (section === "questions") return [
    { title: "Questions I still have", kind: "questions", entries: values.tensions, empty: "No questions have been added yet." },
    { title: "What this could mean for my work", kind: "implications", entries: values.implications, empty: "Nothing has been added here yet." },
  ];
  return [{ title: "What I want to try", kind: "action", entries: values.actionDirection ? [values.actionDirection] : [], empty: "No next step has been added yet." }];
}

/**
 * The three states this page can be in instead of showing a wrap-up: signed out, loading,
 * and unable to load. Each one needs the way back as much as the page itself does — more,
 * in fact, because a teacher who cannot see her wrap-up is the one most likely to want to
 * leave, and "unavailable" is where a tab in the installed application gets stuck.
 */
function ConferenceReviewGate({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return <div className="page conference-review-page"><Link className="back-link subpage-back" href="/my-wvcea">← My WVCEA</Link><section className="personal-empty"><p>My Convention Wrap-Up</p><h1 data-page-heading tabIndex={-1}>{title}</h1><p>{body}</p>{action}</section></div>;
}

async function loadReview(path: string, signal: AbortSignal): Promise<AttendeeConferenceReviewResponse> {
  const response = await fetch(path, { credentials: "same-origin", signal });
  if (!response.ok) throw new Error("Your Convention Wrap-Up could not be loaded.");
  return response.json() as Promise<AttendeeConferenceReviewResponse>;
}

async function responseMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null) as { message?: string } | null;
  return body?.message ?? fallback;
}

function editedSingle(prior: AttendeeConferenceReviewEntry | null, text: string): AttendeeConferenceReviewEntry | null {
  if (!text.trim()) return null;
  return prior && prior.text.trim() === text.trim() ? { ...prior, text } : attendeeEntry(text);
}

function editedList(prior: readonly AttendeeConferenceReviewEntry[], text: string, prefix: string): readonly AttendeeConferenceReviewEntry[] {
  const available = [...prior];
  return text.split("\n").map((value) => value.trim()).filter(Boolean).map((value, index) => {
    const match = available.findIndex((entry) => entry.text.trim() === value);
    if (match < 0) return attendeeEntry(value, `${prefix}-${index}`);
    return available.splice(match, 1)[0]!;
  });
}

function attendeeEntry(text: string, id = "entry"): AttendeeConferenceReviewEntry {
  return { id: `${id}-${crypto.randomUUID()}`, text: text.trim(), sourceItemIds: [], origin: "attendee" };
}

function cleanValues(values: AttendeeConferenceReviewValues): AttendeeConferenceReviewValues {
  const clean = (entry: AttendeeConferenceReviewEntry) => ({ ...entry, text: entry.text.trim() });
  const list = (entries: readonly AttendeeConferenceReviewEntry[]) => entries.filter((entry) => entry.text.trim()).map(clean);
  return { centralDirection: values.centralDirection?.text.trim() ? clean(values.centralDirection) : null,
    themes: list(values.themes), importantIdeas: list(values.importantIdeas), changesInThinking: list(values.changesInThinking),
    tensions: list(values.tensions), implications: list(values.implications),
    actionDirection: values.actionDirection?.text.trim() ? clean(values.actionDirection) : null };
}

function copyValues(values: AttendeeConferenceReviewValues): AttendeeConferenceReviewValues {
  const copy = (entry: AttendeeConferenceReviewEntry) => ({ ...entry, sourceItemIds: [...entry.sourceItemIds] });
  return { centralDirection: values.centralDirection ? copy(values.centralDirection) : null, themes: values.themes.map(copy),
    importantIdeas: values.importantIdeas.map(copy), changesInThinking: values.changesInThinking.map(copy),
    tensions: values.tensions.map(copy), implications: values.implications.map(copy),
    actionDirection: values.actionDirection ? copy(values.actionDirection) : null };
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
}
